import { Checkbox, FormControlLabel, MenuItem, TextField } from '@mui/material';
import { addMinutes, areIntervalsOverlapping, differenceInCalendarDays, isBefore, isEqual } from 'date-fns';
import { useTranslation } from 'next-i18next';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { MdInfoOutline } from 'react-icons/md';
import toast from 'react-hot-toast';
import useAppointment from '../../../hooks/useAppointment';
import type { Appointment, Booth, Meetingroom } from '../../../interfaces/interfaces';
import { useSelector } from '../../../reduxToolkit/typedRedux';
import { createMeetingAppointment, getMeetingAppointments } from '../../../services/services';
import VyButton from '../../common/VyButton';
import VyModal from '../../common/VyModal';
import VySelect from '../../common/VySelect';
import VyTooltip from '../../common/VyTooltip';
import { VyFormError } from '../../common/VyFormError';

interface AppointmentCreateModalProps {
  booth: Booth;
  open: boolean;
  onCreate: () => void;
  onClose: () => void;
}

export const AppointmentCreateModal = ({ booth, onCreate, onClose }: AppointmentCreateModalProps) => {
  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { isSubmitting, errors },
  } = useForm();
  const [overlapError, setOverlapError] = useState('');
  const [multiCreate, setMultiCreate] = useState<boolean>(false);
  const [selectedMeeting, setSelectedMeeting] = useState<string | undefined>(booth?.meetingrooms[0].id);

  const event = useSelector((state) => state.admin.event ?? state.common.event);

  const { defaultDate } = useAppointment(booth._id);
  const { t } = useTranslation(['booth', 'event', 'appointment', 'common']);

  async function createAppointment(data: any) {
    if (!selectedMeeting) return;

    const startDate = new Date(data.date);
    startDate.setHours(data.startTime.slice(0, 2));
    startDate.setMinutes(data.startTime.slice(3, 5));

    const endDate = new Date(data.date);
    endDate.setHours(data.endTime.slice(0, 2));
    endDate.setMinutes(data.endTime.slice(3, 5));

    // Server side check for appointments
    const meetingAppointments = await getMeetingAppointments(selectedMeeting);

    const overlap = meetingAppointments?.find((appointment: Appointment) =>
      areIntervalsOverlapping(
        {
          start: new Date(appointment.datetime_start),
          end: new Date(appointment.datetime_end),
        },
        { start: startDate, end: endDate }
      )
    );

    if (!overlap) {
      const slotsToCreate: { meetingroom: string; datetime_start: Date; datetime_end: Date }[] = [];

      if (data.multicreate === true) {
        let lastEndTime = startDate;
        const slotLength = parseInt(data.slotlength);
        const slotBreak = parseInt(data.slotbreak) || 0;

        if (!slotLength) setOverlapError(t('appointment:errors.no-slotlength'));

        while (
          isBefore(addMinutes(addMinutes(lastEndTime, slotLength), slotBreak), endDate) ||
          isEqual(addMinutes(addMinutes(lastEndTime, slotLength), slotBreak), endDate)
        ) {
          const tempEndTime = addMinutes(lastEndTime, slotLength);

          slotsToCreate.push({
            meetingroom: selectedMeeting,
            datetime_start: lastEndTime,
            datetime_end: tempEndTime,
          });
          lastEndTime = addMinutes(tempEndTime, slotBreak);
        }
      } else {
        slotsToCreate.push({
          meetingroom: selectedMeeting,
          datetime_start: startDate,
          datetime_end: endDate,
        });
      }

      if (slotsToCreate.length === 0) return setOverlapError(t('appointment:errors.no-slots-created'));

      let allCompleted = true;
      for await (const slot of slotsToCreate) {
        try {
          await createMeetingAppointment(slot);
        } catch (err) {
          allCompleted = false;
          console.error(err);
        }
      }

      if (allCompleted === false) toast.custom(t('appointment:errors.not-all-slots-created'), { className: 'warn' });
      else toast.success(t('common:success'));
      onCreate();
    } else setOverlapError(t('appointment:errors.overlap'));
  }

  const startTime = `${event?.modules?.booking?.settings?.start_time && event?.modules?.booking?.settings?.start_time < 10 ? '0' : ''}${
    event?.modules?.booking?.settings?.start_time ?? '08'
  }:00`;
  const endTime = `${event?.modules?.booking?.settings?.end_time && event?.modules?.booking?.settings?.end_time < 10 ? '0' : ''}${
    event?.modules?.booking?.settings?.end_time ?? '20'
  }:00`;

  return (
    <VyModal open onClose={() => onClose()}>
      {() => (
        <form onBlur={() => setOverlapError('')} className="flex flex-col w-full" onSubmit={handleSubmit(createAppointment)} noValidate>
          {booth && event && booth?.meetingrooms && (
            <>
              <div
                className={`my-3 
              ${!(differenceInCalendarDays(new Date(event.datetime_end), new Date(event.datetime_start)) > 0) && 'hidden'}
              `}>
                <span>{t('appointment:appointment-day')}</span>
                <Controller
                  control={control}
                  name="date"
                  defaultValue={defaultDate(event?.datetime_start, event?.datetime_end)}
                  rules={{ required: { value: true, message: 'Date is missing' } }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      id="date"
                      type="date"
                      variant="outlined"
                      color="secondary"
                      inputProps={{
                        min: event?.datetime_start?.slice(0, 10),
                        max: event?.datetime_end?.slice(0, 10),
                        style: { width: '100%' },
                      }}
                    />
                  )}
                />
              </div>
              <div>
                <FormControlLabel
                  control={<Checkbox checked={multiCreate} onChange={() => setMultiCreate(!multiCreate)} name="multicreate" />}
                  label={t('appointment:multicreate')}
                  {...register('multicreate')}
                />
              </div>

              {multiCreate === true && (
                <div className="flex flex-col space-y-2">
                  <Controller
                    control={control}
                    name="slotlength"
                    render={({ field }) => (
                      <TextField
                        {...field}
                        id="standard-number"
                        label={t('appointment:slotlength')}
                        type="number"
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="slotbreak"
                    render={({ field }) => (
                      <TextField
                        {...field}
                        id="standard-number"
                        label={t('appointment:slotbreak')}
                        type="number"
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    )}
                  />
                </div>
              )}

              <div className="mt-3" />
              <span>
                {t('appointment:start-from', {
                  start: startTime,
                })}
              </span>
              <Controller
                control={control}
                name="startTime"
                rules={{
                  validate: {
                    respectEventStart: (value) => booth != undefined && value >= startTime,
                    respectEventEnd: (value) => booth != undefined && value <= endTime,
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    id="date"
                    type="time"
                    variant="outlined"
                    color="secondary"
                    inputProps={{
                      min: startTime,
                      max: endTime,
                      step: 1800,
                    }}
                  />
                )}
              />
              <div className="mt-3 space-y-2" />
              <span>{t('appointment:end-until', { end: endTime })} </span>
              <Controller
                control={control}
                name="endTime"
                rules={{
                  validate: {
                    lessThanStart: (value) => watch('startTime') && new Date(`2011-04-20T${value}`) > new Date(`2011-04-20T${watch('startTime')}`),
                    respectEventStart: (value) => booth != undefined && value >= startTime,
                    respectEventEnd: (value) => booth != undefined && value <= endTime,
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    id="date"
                    type="time"
                    error={!!errors?.endTime?.type}
                    variant="outlined"
                    color="secondary"
                    inputProps={{
                      min: startTime,
                      max: endTime,
                      step: 1800,
                    }}
                  />
                )}
              />
              {errors?.endTime?.type === 'lessThanStart' && <VyFormError message={t('appointment:error-endtime')} />}
              <div className="mt-8" />
              <span className="flex items-center justify-between">
                {event?.template.individualNames.meetingrooms}
                <VyTooltip title={t('appointment:appointment-needs-contactperson')}>
                  <MdInfoOutline size={20} />
                </VyTooltip>
              </span>
              <div className="mt-4" />
              <VySelect
                fullWidth
                defaultValue={selectedMeeting}
                //@ts-ignore
                onChange={(e) => setSelectedMeeting(e.target.value)}>
                {booth?.meetingrooms.map((meetingroom: Meetingroom, index: number) => (
                  <MenuItem value={meetingroom.id} disabled={!meetingroom.contactperson}>
                    <p>{meetingroom.contactperson ?? `${t('account:meeting-types.meeting')} ${index + 1}`}</p>
                  </MenuItem>
                ))}
              </VySelect>
              <div className="mt-8" />
              <VyFormError
                message={
                  errors?.date?.message?.toString() || (errors?.endTime || errors?.startTime ? t('appointment:error-time-incorrect') : '') || overlapError
                }
              />

              <VyButton type="submit" style={{ width: '100%' }} disabled={isSubmitting}>
                {t('appointment:add-appointment-slot')}
              </VyButton>
            </>
          )}
        </form>
      )}
    </VyModal>
  );
};
